import { atom } from "jotai";
import { User } from "firebase/auth";

import { CURRENT_PLATFORM } from "../../configs/platform.generated";

import { AssetResponse } from "@/utils/FetchAssetDetails/types";

/**
 * A Jotai atom that controls the visibility of the Lottie animation.
 *
 * @remarks
 * This atom holds a boolean value:
 * - `true` means the animation should be visible.
 * - `false` means the animation should be hidden.
 *
 * Components using this atom can subscribe to it via `useAtom` to respond to changes in visibility.
 */
export const showLottieAtom = atom(true);

export const platformAtom = atom<"tv" | "web" | "mobile">(CURRENT_PLATFORM);

/**
 * A Jotai atom that tracks if any modal is currently open.
 *
 * @remarks
 * This atom holds a boolean value:
 * - `true` means a modal is open and body scroll should be prevented.
 * - `false` means no modal is open and body scroll is allowed.
 *
 * Components can use this atom to control body scroll prevention globally.
 */
export const isModalOpenAtom = atom(false);

/**
 * A Jotai atom that holds the currently selected asset.
 *
 * @remarks
 * This atom stores the selected AssetResponse object or null:
 * - `AssetResponse` when an asset is selected (e.g., for video playback).
 * - `null` when no asset is selected.
 *
 * Components can use this atom to share selected asset state globally.
 */
export const selectedAssetAtom = atom<AssetResponse | null>(null);

/**
 * A Jotai atom that holds the current authenticated user.
 *
 * @remarks
 * This atom stores the Firebase User object or null:
 * - `User` when a user is authenticated.
 * - `null` when no user is authenticated.
 *
 * Components can use this atom to access current user state globally.
 */
export const currentUserAtom = atom<User | null>(null);
